import React from 'react'
import PropTypes from 'prop-types'
import Modal from 'components/mui/Modal'
import Typography from 'components/mui/Typography'
import AppButtonComponent from 'components/mui/AppButtonComponent'
import Box from 'components/mui/Box'
import Stack from 'components/mui/Stack'
import { useTheme, alpha } from '@mui/material'
import { typography } from 'theme'

/**
 * PAN Fetch Error Modal - Displays error when PAN details fetch fails
 * @component
 * @param {Object} props - Component props
 * @returns {React.ReactElement} PanFetchErrorModal
 */
const PanFetchErrorModal = ({
    open,
    onClose,
    onRetry,
    onContactRM,
    title = "Fetch failed",
    message = "PAN details unavailable. Please retry or enter manually.",
    retryText = "Retry",
    contactText = "Contact RM"
}) => {
    const theme = useTheme();
    // Modal styles
    const modalStyle = {
        // width: { xs: '90%', sm: '400px' },
        // maxWidth: '400px',
        bgcolor: '#1A1D29',
        borderRadius: '12px',
        p: 3,
        gap: 4,
        position: 'relative',
        textAlign: 'center'
    }

    const titleStyle = {
        color: '#FFFFFF',
        fontSize: '20px',
        fontWeight: '600',
        marginBottom: '4px',
        lineHeight: '1.2'
    }

    const messageStyle = {
        color: '#FFFFFF',
        fontSize: '14px',
        fontWeight: '400',
        // marginBottom: '30px',
        lineHeight: '1.4'
    }

    const iconContainerStyle = {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative'
    }

    const errorIconStyle = {
        width: '80px',
        height: '80px',
        borderRadius: '50%',
        backgroundColor: '#E74C3C',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        '&::before': {
            content: '""',
            position: 'absolute',
            width: '100px',
            height: '100px',
            borderRadius: '50%',
            backgroundColor: 'rgba(231, 76, 60, 0.1)',
            zIndex: -1
        }
    }

    const xIconStyle = {
        color: '#FFFFFF',
        fontSize: '32px',
        fontWeight: 'bold',
        lineHeight: 1
    }

    // const particlesStyle = {
    //     position: 'absolute',
    //     width: '120px',
    //     height: '120px',
    //     '&::before': {
    //         content: '""',
    //         position: 'absolute',
    //         width: '4px',
    //         height: '4px',
    //         backgroundColor: '#FFFFFF',
    //         borderRadius: '50%',
    //         top: '10%',
    //         left: '20%',
    //         opacity: 0.6
    //     },
    //     '&::after': {
    //         content: '""',
    //         position: 'absolute',
    //         width: '3px',
    //         height: '3px',
    //         backgroundColor: '#FFFFFF',
    //         borderRadius: '50%',
    //         top: '30%',
    //         right: '15%',
    //         opacity: 0.4
    //     }
    // }

    const buttonContainerStyle = {
        display: 'flex',
        gap: '16px',
        justifyContent: 'center'
    }

    const contactButtonStyle = {
        backgroundColor: 'transparent',
        border: `1px solid ${theme.palette.common.white}`,
        borderColor: theme.palette.common.white,
        color: theme.palette.common.white,
        borderRadius: theme.spacing(1),
        padding: theme.spacing(1.5, 2.75),
        fontSize: typography.fontSize.buttonMedium,
        fontWeight: typography.fontWeight.medium,
        textTransform: 'none',
        minWidth: '120px',
        minHeight: 48,
        '&:hover': {
            backgroundColor: alpha(theme.palette.common.white, 0.1),
            borderColor: theme.palette.common.white
        }
    }

    const retryButtonStyle = {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.custom?.text?.buttonOnPrimary || theme.palette.common.white,
        borderRadius: theme.spacing(1),
        padding: theme.spacing(1.5, 2.75),
        fontSize: typography.fontSize.buttonMedium,
        fontWeight: typography.fontWeight.medium,
        textTransform: 'none',
        minWidth: '120px',
        minHeight: 48,
        border: 'none',
        '&:hover': {
            backgroundColor: theme.palette.primary.dark
        }
    }

    return (
        <Modal open={open} onClose={onClose}>
            <Stack sx={modalStyle}>
                {/* Title */}
                <Stack direction="column" alignItems="center" justifyContent="center">
                    <Typography sx={titleStyle}>
                        {title}
                    </Typography>

                    {/* Message */}
                    <Typography sx={messageStyle}>
                        {message}
                    </Typography>
                </Stack>

                {/* Error Icon with Particles */}
                <Box sx={iconContainerStyle}>
                    {/* <Box sx={particlesStyle} /> */}
                    <Box sx={errorIconStyle}>
                        <Typography sx={xIconStyle}>
                            ✕
                        </Typography>
                    </Box>
                </Box>

                {/* Action Buttons */}
                <Box sx={buttonContainerStyle}>
                    <AppButtonComponent
                        sx={contactButtonStyle}
                        onClick={onContactRM}
                        variant="outlined"
                    >
                        {contactText}
                    </AppButtonComponent>

                    <AppButtonComponent
                        sx={retryButtonStyle}
                        onClick={onRetry}
                        variant="contained"
                    >
                        {retryText}
                    </AppButtonComponent>
                </Box>
            </Stack>
        </Modal>
    )
}

PanFetchErrorModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onRetry: PropTypes.func.isRequired,
    onContactRM: PropTypes.func.isRequired,
    title: PropTypes.string,
    message: PropTypes.string,
    retryText: PropTypes.string,
    contactText: PropTypes.string
}

PanFetchErrorModal.defaultProps = {
    title: "Fetch failed",
    message: "PAN details unavailable. Please retry or enter manually.",
    retryText: "Retry",
    contactText: "Contact RM"
}

export default PanFetchErrorModal
